import { c as _c } from "react/compiler-runtime";
import { TextSkeleton } from '@udemy-v2/react-reveal-components';
import { Carousel } from '@udemy-v2/react-structure-components';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import classNames from 'classnames';
import { publishEvent } from '@udemy-v2/event-tracking';
import { useOnCarouselScrollEnd } from '@/lib/hooks/use-on-carousel-scroll-end';
import { Review } from '@/types/review';
import { ReviewsModalButton } from './review-modal/reviews-modal-button.react-component';
import { ReviewsModalContainer } from './review-modal/reviews-modal-container.react-component';
import { ReviewTitle } from './review-title.react-component';
import { Review as ReviewComponent } from './review/review.react-component';
import { ReviewSkeleton } from './review/review-skeleton.react-component';
import styles from './reviews.module.scss';
import type { ReviewsConfig, ReviewsContext } from './types';
import { ReviewsCarouselScrollEvent, SeeMoreReviewsClickEvent } from '../../events/events';
import { useClpContext } from '../../context/clp-context-provider.react-component';
import { useClpCourse } from '../../context/clp-course-provider.react-component';
import { useI18n } from '@udemy-v2/i18n';
export interface ReviewsProps {
  reviews: Review[];
  isLoading: boolean;
  reviewsRatings?: ReviewsContext;
  reviewableObjectId: number;
  reviewableObjectType: string;
  className?: string;
  sectionHeaderClassName?: string;
  mobileCarouselClassName?: string;
  tabletCarouselClassName?: string;
  scrollTargetClassName?: string;
  courseId: number;
}
export const noReviewsMessage = (store: ReviewsConfig, gettext: (text: string) => string) => {
  if (!store.rating) {
    if (!store.searchText) {
      return {
        purpose: 'no-comments-message',
        message: gettext('There are no written comments for this course yet.')
      };
    }
    return {
      purpose: 'no-matched-comments-message',
      message: gettext('No reviews matched your search. Try searching with another term.')
    };
  }
  if (!store.searchText) {
    return {
      purpose: 'no-selected-comments-message',
      message: gettext("There are no written comments for the rating you've selected. " + 'Please select another rating or clear your selection to view ' + 'all written comments for this course.')
    };
  }
  return {
    purpose: 'no-selected-matched-comments-message',
    message: gettext('Your search returned no results with the selected rating. ' + 'Try clearing your selection to see reviews matching your search.')
  };
};
export const Reviews = t0 => {
  const $ = _c(70);
  const {
    reviews,
    isLoading,
    reviewsRatings,
    reviewableObjectId,
    reviewableObjectType,
    className,
    sectionHeaderClassName,
    mobileCarouselClassName,
    tabletCarouselClassName,
    scrollTargetClassName,
    courseId
  } = t0;
  const {
    isReviewsModalOpen,
    openReviewsModal,
    closeReviewsModal
  } = useClpContext();
  const course = useClpCourse();
  const isXsMax = useMatchMedia("xs-max");
  const isSmMin = useMatchMedia("sm-min");
  const isSmMax = useMatchMedia("sm-max");
  const {
    gettext
  } = useI18n();
  let t1;
  if ($[0] !== course.frontendTrackingId || $[1] !== courseId || $[2] !== isReviewsModalOpen || $[3] !== openReviewsModal) {
    t1 = () => {
      if (!isReviewsModalOpen && course.frontendTrackingId) {
        publishEvent(new SeeMoreReviewsClickEvent({
          courseTrackingId: course.frontendTrackingId,
          reviewableObjectId: courseId,
          reviewableObjectType: "course"
        }));
      }
      openReviewsModal();
    };
    $[0] = course.frontendTrackingId;
    $[1] = courseId;
    $[2] = isReviewsModalOpen;
    $[3] = openReviewsModal;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleOnModalTriggerButtonClick = t1;
  const t2 = isXsMax ?? false;
  let t3;
  if ($[5] !== isXsMax || $[6] !== reviewsRatings || $[7] !== sectionHeaderClassName) {
    t3 = () => reviewsRatings ? <ReviewTitle reviewsContext={reviewsRatings} isXsMax={isXsMax ?? false} /> : <TextSkeleton className={classNames(styles["reviews-title"], sectionHeaderClassName)} paragraphCount={0} withTitle={true} />;
    $[5] = isXsMax;
    $[6] = reviewsRatings;
    $[7] = sectionHeaderClassName;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = !isXsMax && (isSmMin ?? false);
  let t5;
  if ($[9] !== closeReviewsModal || $[10] !== course.frontendTrackingId || $[11] !== isReviewsModalOpen || $[12] !== reviewableObjectId || $[13] !== reviewableObjectType || $[14] !== reviewsRatings || $[15] !== t2 || $[16] !== t3 || $[17] !== t4) {
    t5 = <ReviewsModalContainer reviewsRatings={reviewsRatings} frontendTrackingId={course.frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} isXsMax={t2} isOpen={isReviewsModalOpen} renderTitle={t3} onClose={closeReviewsModal} shouldUseTwoColumnLayout={t4} />;
    $[9] = closeReviewsModal;
    $[10] = course.frontendTrackingId;
    $[11] = isReviewsModalOpen;
    $[12] = reviewableObjectId;
    $[13] = reviewableObjectType;
    $[14] = reviewsRatings;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const reviewsModal = t5;
  if (isLoading || !reviewsRatings) {
    let t6;
    if ($[19] !== sectionHeaderClassName) {
      t6 = classNames(styles["reviews-title"], sectionHeaderClassName);
      $[19] = sectionHeaderClassName;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    let t7;
    if ($[21] !== t6) {
      t7 = <TextSkeleton className={t6} paragraphCount={0} withTitle={true} />;
      $[21] = t6;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    let t8;
    if ($[23] !== isSmMax || $[24] !== isXsMax || $[25] !== mobileCarouselClassName || $[26] !== tabletCarouselClassName) {
      t8 = isSmMax ? <Carousel allowScroll={true} ariaLabel="Course reviews" data-purpose="reviews-mobile-carousel-layout" showPager={!isXsMax} pagerButtonSize="medium" className={classNames(mobileCarouselClassName, !isXsMax ? tabletCarouselClassName : null, styles.loading)}><ReviewSkeleton className={styles.review} /><ReviewSkeleton className={styles.review} /></Carousel> : <div data-purpose="reviews-desktop-layout" className={styles["reviews-desktop"]}><ReviewSkeleton className={styles.review} isDesktopLayout={true} /><ReviewSkeleton className={styles.review} isDesktopLayout={true} /><ReviewSkeleton className={styles.review} isDesktopLayout={true} /><ReviewSkeleton className={styles.review} isDesktopLayout={true} /></div>;
      $[23] = isSmMax;
      $[24] = isXsMax;
      $[25] = mobileCarouselClassName;
      $[26] = tabletCarouselClassName;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t9;
    if ($[28] !== className || $[29] !== reviewsModal || $[30] !== t7 || $[31] !== t8) {
      t9 = <div data-purpose="reviews-loading" id="reviews" className={className}>{t7}{t8}{reviewsModal}</div>;
      $[28] = className;
      $[29] = reviewsModal;
      $[30] = t7;
      $[31] = t8;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    return t9;
  }
  if (!reviews || reviews.length === 0) {
    return null;
  }
  let t6;
  if ($[33] !== course.frontendTrackingId || $[34] !== courseId || $[35] !== handleOnModalTriggerButtonClick || $[36] !== isSmMax || $[37] !== isXsMax || $[38] !== mobileCarouselClassName || $[39] !== reviewableObjectId || $[40] !== reviewableObjectType || $[41] !== reviews || $[42] !== tabletCarouselClassName) {
    t6 = isSmMax ? <ReviewsContentMobile reviews={reviews} frontendTrackingId={course.frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} isXsMax={isXsMax ?? false} mobileCarouselClassName={mobileCarouselClassName} tabletCarouselClassName={tabletCarouselClassName} onModalTriggerButtonClick={handleOnModalTriggerButtonClick} courseId={courseId} /> : <ReviewsContentDesktop reviews={reviews} frontendTrackingId={course.frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} onModalTriggerButtonClick={handleOnModalTriggerButtonClick} />;
    $[33] = course.frontendTrackingId;
    $[34] = courseId;
    $[35] = handleOnModalTriggerButtonClick;
    $[36] = isSmMax;
    $[37] = isXsMax;
    $[38] = mobileCarouselClassName;
    $[39] = reviewableObjectId;
    $[40] = reviewableObjectType;
    $[41] = reviews;
    $[42] = tabletCarouselClassName;
    $[43] = t6;
  } else {
    t6 = $[43];
  }
  const t7 = course.frontendTrackingId;
  const t8 = isXsMax ?? false;
  let t9;
  if ($[44] !== gettext) {
    t9 = gettext("Show all reviews");
    $[44] = gettext;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== course.frontendTrackingId || $[47] !== handleOnModalTriggerButtonClick || $[48] !== reviewableObjectId || $[49] !== reviewableObjectType || $[50] !== t8 || $[51] !== t9) {
    t10 = <ReviewsModalButton frontendTrackingId={t7} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} fullWidth={t8} onClick={handleOnModalTriggerButtonClick} className={styles["trigger-button-container"]} label={t9} />;
    $[46] = course.frontendTrackingId;
    $[47] = handleOnModalTriggerButtonClick;
    $[48] = reviewableObjectId;
    $[49] = reviewableObjectType;
    $[50] = t8;
    $[51] = t9;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  let t11;
  if ($[53] !== reviewsModal || $[54] !== t10 || $[55] !== t6) {
    t11 = <>{t6}{t10}{reviewsModal}</>;
    $[53] = reviewsModal;
    $[54] = t10;
    $[55] = t6;
    $[56] = t11;
  } else {
    t11 = $[56];
  }
  const content = t11;
  const t12 = scrollTargetClassName ?? "";
  let t13;
  if ($[57] !== t12) {
    t13 = <span className={t12} id="reviews" />;
    $[57] = t12;
    $[58] = t13;
  } else {
    t13 = $[58];
  }
  const t14 = isXsMax ?? false;
  let t15;
  if ($[59] !== sectionHeaderClassName) {
    t15 = classNames(styles["reviews-title"], sectionHeaderClassName);
    $[59] = sectionHeaderClassName;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  let t16;
  if ($[61] !== reviewsRatings || $[62] !== t14 || $[63] !== t15) {
    t16 = <ReviewTitle reviewsContext={reviewsRatings} isXsMax={t14} className={t15} />;
    $[61] = reviewsRatings;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] !== className || $[66] !== content || $[67] !== t13 || $[68] !== t16) {
    t17 = <div data-purpose="reviews" className={className}>{t13}{t16}{content}</div>;
    $[65] = className;
    $[66] = content;
    $[67] = t13;
    $[68] = t16;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  return t17;
};
interface ReviewsContentMobileProps {
  reviews: Review[];
  frontendTrackingId?: string;
  reviewableObjectId: number;
  reviewableObjectType: string;
  isXsMax: boolean;
  mobileCarouselClassName?: string;
  tabletCarouselClassName?: string;
  onModalTriggerButtonClick: () => void;
  courseId: number;
}
const ReviewsContentMobile = t0 => {
  const $ = _c(19);
  const {
    reviews,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    isXsMax,
    mobileCarouselClassName,
    tabletCarouselClassName,
    onModalTriggerButtonClick,
    courseId
  } = t0;
  const course = useClpCourse();
  const carouselScroll = useOnCarouselScrollEnd(trackCarouselScroll);
  function trackCarouselScroll() {
    if (course.frontendTrackingId) {
      publishEvent(new ReviewsCarouselScrollEvent({
        courseTrackingId: course.frontendTrackingId,
        reviewableObjectId: courseId,
        reviewableObjectType: "course"
      }));
    }
  }
  if (reviews.length === 0) {
    return null;
  }
  const t1 = !isXsMax;
  const t2 = !isXsMax ? tabletCarouselClassName : null;
  let t3;
  if ($[0] !== mobileCarouselClassName || $[1] !== t2) {
    t3 = classNames(mobileCarouselClassName, t2);
    $[0] = mobileCarouselClassName;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = carouselScroll.props;
  let t5;
  if ($[3] !== frontendTrackingId || $[4] !== onModalTriggerButtonClick || $[5] !== reviewableObjectId || $[6] !== reviewableObjectType || $[7] !== reviews) {
    let t6;
    if ($[9] !== frontendTrackingId || $[10] !== onModalTriggerButtonClick || $[11] !== reviewableObjectId || $[12] !== reviewableObjectType) {
      t6 = review => <ReviewComponent key={review.id} review={review} frontendTrackingId={frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} className={styles.review} shouldUseInlineShowMore={false} shouldUseDeattachedDropdown={true} instructorResponseVisibility="seo-only" onShowMoreModalTriggerClick={onModalTriggerButtonClick} isInMobileCarousel={true} />;
      $[9] = frontendTrackingId;
      $[10] = onModalTriggerButtonClick;
      $[11] = reviewableObjectId;
      $[12] = reviewableObjectType;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t5 = reviews.map(t6);
    $[3] = frontendTrackingId;
    $[4] = onModalTriggerButtonClick;
    $[5] = reviewableObjectId;
    $[6] = reviewableObjectType;
    $[7] = reviews;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[14] !== carouselScroll.props || $[15] !== t1 || $[16] !== t3 || $[17] !== t5) {
    t6 = <div><Carousel allowScroll={true} ariaLabel="Course reviews" data-purpose="reviews-mobile-carousel-layout" showPager={t1} pagerButtonSize="medium" gridMode={true} gridFullWidthItems={true} gridSize="medium" className={t3} {...t4}>{t5}</Carousel></div>;
    $[14] = carouselScroll.props;
    $[15] = t1;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
interface ReviewsContentDesktopProps {
  reviews: Review[];
  frontendTrackingId?: string;
  reviewableObjectId: number;
  reviewableObjectType: string;
  onModalTriggerButtonClick: () => void;
}
export const DESKTOP_MAX_NUMBER_OF_DISPLAYED_REVIEWS = 4;
const ReviewsContentDesktop = t0 => {
  const $ = _c(13);
  const {
    reviews,
    frontendTrackingId,
    reviewableObjectId,
    reviewableObjectType,
    onModalTriggerButtonClick
  } = t0;
  let t1;
  if ($[0] !== frontendTrackingId || $[1] !== onModalTriggerButtonClick || $[2] !== reviewableObjectId || $[3] !== reviewableObjectType || $[4] !== reviews) {
    let t2;
    if ($[6] !== frontendTrackingId || $[7] !== onModalTriggerButtonClick || $[8] !== reviewableObjectId || $[9] !== reviewableObjectType) {
      t2 = review => <ReviewComponent key={review.id} review={review} frontendTrackingId={frontendTrackingId} reviewableObjectType={reviewableObjectType} reviewableObjectId={reviewableObjectId} className={styles.review} isDesktopLayout={true} shouldUseInlineShowMore={false} instructorResponseVisibility="seo-only" onShowMoreModalTriggerClick={onModalTriggerButtonClick} />;
      $[6] = frontendTrackingId;
      $[7] = onModalTriggerButtonClick;
      $[8] = reviewableObjectId;
      $[9] = reviewableObjectType;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = reviews.slice(0, DESKTOP_MAX_NUMBER_OF_DISPLAYED_REVIEWS).map(t2);
    $[0] = frontendTrackingId;
    $[1] = onModalTriggerButtonClick;
    $[2] = reviewableObjectId;
    $[3] = reviewableObjectType;
    $[4] = reviews;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[11] !== t1) {
    t2 = <div data-purpose="reviews-desktop-layout" className={styles["reviews-desktop"]}>{t1}</div>;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
};
interface NoReviewsMessageProps {
  message: {
    purpose: string;
    message: string;
  };
}
export const NoReviewsMessage = t0 => {
  const $ = _c(4);
  const {
    message
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = classNames("ud-text-md", styles["no-reviews-message"]);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== message.message || $[2] !== message.purpose) {
    t2 = <p className={t1} data-testid={message.purpose}>{message.message}</p>;
    $[1] = message.message;
    $[2] = message.purpose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};